package com.advaita.cloud.drive.ui.store

import com.advaita.cloud.drive.shared.S3Credential

/**
 * Split a pasted block into clean URLs: one per line, trimmed, blanks dropped,
 * de-duped (order-preserving). Moved out of [FileBrowserStore] (unchanged body) so
 * the store and the pull modal's live bucket-detection can both call it without a
 * FileBrowserStore instance.
 */
fun parsePullUrls(raw: String): List<String> =
    raw.split('\n', '\r')
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .distinct()

/**
 * The s3:// bucket named by one URL, or null if it isn't an s3:// URL / has no bucket.
 * `s3://bucket/key...` -> "bucket". Deliberately simple string parsing — there's no
 * java.net.URI on the JS target — mirroring the bucket-extraction the backend does in
 * PullService/S3Fetcher.
 */
fun s3BucketOf(url: String): String? {
    if (!url.startsWith("s3://")) return null
    val bucket = url.removePrefix("s3://").substringBefore('/')
    return bucket.takeIf { it.isNotBlank() }
}

/** Distinct s3:// buckets across [urls], in first-seen order (drives block order + the "N buckets" copy). */
fun distinctS3Buckets(urls: List<String>): List<String> = urls.mapNotNull(::s3BucketOf).distinct()

/** How many s3:// urls name each bucket — the "· N objects" annotation in the step-2 view (spec §5.2). */
fun bucketObjectCounts(urls: List<String>): Map<String, Int> =
    urls.mapNotNull(::s3BucketOf).groupingBy { it }.eachCount()

/**
 * Threshold for the credential wizard: **more than one** distinct bucket. Revised from the
 * spec's original "3+" after S5 qa vetting on real CoreWeave — two stacked credential blocks
 * already overflowed the inline modal (the reassurance copy plus two full key blocks pushed
 * "Pull (2)" to the bottom of the viewport), so only the single-bucket case stays inline.
 */
fun needsStepFlow(buckets: List<String>): Boolean = buckets.size > 1

/**
 * True when the pasted URLs name an s3:// bucket but the server has the s3 connector OFF
 * (`authConfig.s3PullEnabled == false`). The modal then hides the AWS-keys panel entirely,
 * shows a "not enabled" notice, and keeps the Pull button disabled — all-or-nothing, mirroring
 * the backend's admission reject (an s3:// line blocks the whole submit rather than partial-
 * submitting the http/https lines). Gating the credential UI on the sub-flag is the S6 M1 fix:
 * previously the panel appeared purely from bucket detection, with no server capability check.
 */
fun s3PullBlocked(buckets: List<String>, s3PullEnabled: Boolean): Boolean =
    buckets.isNotEmpty() && !s3PullEnabled

/** Which screen of the wizard is showing. Only meaningful when [needsStepFlow] is true —
 *  the inline (single-bucket) layout ignores it and always shows everything at once. */
enum class PullModalStep { URLS, CREDENTIALS }

/**
 * How many credential pages the wizard has: **one per bucket**, so a page asks for exactly
 * one bucket's keys instead of stacking every block on one screen. "Use one key set for all
 * buckets" overrides the pagination entirely — it collapses to a single page.
 */
fun credentialPageCount(buckets: List<String>, useSharedKeys: Boolean): Int = when {
    buckets.isEmpty() -> 0
    useSharedKeys -> 1
    else -> buckets.size
}

/**
 * Hold a page index in range. Needed because the page count changes *under* the user: editing
 * the URL list can drop buckets, and ticking "use one key set" collapses N pages to 1.
 */
fun clampCredentialPage(page: Int, pageCount: Int): Int =
    if (pageCount <= 0) 0 else page.coerceIn(0, pageCount - 1)

/** The bucket whose keys page [page] collects; null in shared mode (its one page covers all). */
fun credentialPageBucket(buckets: List<String>, useSharedKeys: Boolean, page: Int): String? =
    if (useSharedKeys) null else buckets.getOrNull(page)

/** True on the final credential page — where the primary button becomes "Pull (N)". */
fun isLastCredentialPage(page: Int, pageCount: Int): Boolean = page >= pageCount - 1

/** "Bucket 2 of 3" progress counter, or null when there is nothing to paginate (0 or 1 page). */
fun credentialPageCounter(page: Int, pageCount: Int): String? =
    if (pageCount <= 1) null else "Bucket ${page + 1} of $pageCount"

/**
 * Whether the CURRENT page alone is complete. The wizard gates "Next" per page so a user can't
 * skip past a bucket and discover the omission only at the end — distinct from
 * [allBucketsFilled], which gates the final submit.
 */
fun credentialPageFilled(
    buckets: List<String>,
    useSharedKeys: Boolean,
    shared: BucketKeyInput,
    perBucket: Map<String, BucketKeyInput>,
    page: Int,
): Boolean {
    if (buckets.isEmpty()) return true
    if (useSharedKeys) return shared.isFilled()
    val bucket = credentialPageBucket(buckets, useSharedKeys = false, page = page) ?: return true
    return perBucket[bucket]?.isFilled() == true
}

/**
 * One progress dot per credential page, so the user can see at a glance how many buckets are
 * still to do. [filled] = that page's keys are complete; [current] = the page being shown.
 * Empty when there's only one page (nothing to indicate).
 */
data class CredentialDot(val filled: Boolean, val current: Boolean)

fun credentialDots(
    buckets: List<String>,
    useSharedKeys: Boolean,
    shared: BucketKeyInput,
    perBucket: Map<String, BucketKeyInput>,
    page: Int,
): List<CredentialDot> {
    val pageCount = credentialPageCount(buckets, useSharedKeys)
    if (pageCount <= 1) return emptyList()
    return (0 until pageCount).map { i ->
        CredentialDot(
            filled = credentialPageFilled(buckets, useSharedKeys, shared, perBucket, i),
            current = i == page,
        )
    }
}

/**
 * One bucket's credential fields as typed by the user, plus the session-token
 * disclosure's expand state — the secret half of an [S3Credential] (bucket is supplied
 * separately: it's a map key driven by the distinct buckets detected in the URL list,
 * never typed by the user).
 */
data class BucketKeyInput(
    val accessKeyId: String = "",
    val secretAccessKey: String = "",
    val sessionToken: String = "",
    val sessionTokenExpanded: Boolean = false,
) {
    fun isFilled(): Boolean = accessKeyId.isNotBlank() && secretAccessKey.isNotBlank()
}

/**
 * True once every detected bucket has filled keys — or, in shared mode, the single
 * shared block is filled — driving the modal's submit-disabled state. A bucket with no
 * credential input at all (never typed into) counts as unfilled, not absent.
 */
fun allBucketsFilled(
    buckets: List<String>,
    useSharedKeys: Boolean,
    shared: BucketKeyInput,
    perBucket: Map<String, BucketKeyInput>,
): Boolean {
    if (buckets.isEmpty()) return true
    return if (useSharedKeys) shared.isFilled() else buckets.all { perBucket[it]?.isFilled() == true }
}

/**
 * Build the wire-contract [S3Credential] list for [buckets]. The wire contract is
 * ALWAYS per-bucket (spec §4/§5.3) — "Use one key set for all buckets" is purely a UI
 * shortcut that fans [shared] out into one identical S3Credential per bucket here.
 */
fun buildS3Credentials(
    buckets: List<String>,
    useSharedKeys: Boolean,
    shared: BucketKeyInput,
    perBucket: Map<String, BucketKeyInput>,
): List<S3Credential> = buckets.map { bucket ->
    val input = if (useSharedKeys) shared else (perBucket[bucket] ?: BucketKeyInput())
    S3Credential(
        bucket = bucket,
        accessKeyId = input.accessKeyId,
        secretAccessKey = input.secretAccessKey,
        sessionToken = input.sessionToken.takeIf { it.isNotBlank() },
    )
}

/** What the Pull-from-URL modal resolves with on a real submit; null means cancelled. */
data class PullSubmission(val urls: List<String>, val s3Credentials: List<S3Credential>)
